/* eslint-disable @typescript-eslint/ban-types */
import { useCallback, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import TableHead from './TableHead';
import TableRow from './TableRow';
import { ColumnSetting, makeColumnClassName } from './utils';
import Button from 'components/inputs/Button';

interface TableWrapperProps {
  columnSettings: ColumnSetting<any, any>[];
  columnWidthAsFlexAmount?: boolean;
  fluid?: boolean;
}

const TableWrapper = styled.div<TableWrapperProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ columnSettings, columnWidthAsFlexAmount }) => {
    const totalColumnWidth = columnSettings.reduce(
      (acc, setting) => acc + setting.width,
      0
    );
    return css`
      & > * {
        ${!columnWidthAsFlexAmount ? `width: ${totalColumnWidth}px;` : ''}
      }
      ${columnWidthAsFlexAmount
        ? columnSettings.map(
            (cs, i) => css`
              .${makeColumnClassName(i)} {
                flex: ${cs.width};
                border-right: 1px solid
                  ${({ theme }) => theme.colors.border.light};
                ${cs.css}
              }
            `
          )
        : columnSettings.map(
            (cs, i) => css`
              .${makeColumnClassName(i)} {
                flex-basis: ${(cs.width / totalColumnWidth) * 100}%;
                box-sizing: border-box;
                min-width: ${cs.width}px;
                width: ${cs.width}px;
                border-right: 1px solid
                  ${({ theme }) => theme.colors.border.light};
                ${cs.css}
              }
            `
          )}
    `;
  }};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
`;

const PaginationButton = styled(Button)`
  margin: 0 5px;
`;

const PageButton = styled(PaginationButton)<{ selected: boolean }>`
  ${({ selected, theme }) => css`
    background-color: ${selected
      ? theme.colors.background.primary
      : 'transparent'};
    color: ${theme.colors.foreground.primary};
    border: 1px solid ${theme.colors.border.light};
    font-weight: ${selected ? 'bold' : 'normal'};
  `}
`;

export interface SortState {
  ascending: boolean;
  sortedColumnIndex: number;
}

interface Props<Row extends Object, RenderProps extends Object> {
  columnSettings: ColumnSetting<Row, RenderProps>[];
  renderProps?: RenderProps;
  rows: Row[];
  initialSortState?: SortState;
  useColumnWidthAsFlexAmount?: boolean;
  onRowClick?(row: Row): void;
  onSortStateChange?(sortState: SortState): void;
  /** creates the className for the row, also used for the row key */
  rowClassName?: (row: Row) => string;
  className?: string;
  fluid?: boolean;
  currentPage?: number;
  totalPageCount?: number;
  pageSize?: number;
  onGoToPage?(pageNumber: number): void;
  showCustomTableHead?: boolean;
  // Props for grouping:
  groupBy?: (row: Row) => string;
  groupHeader?: (groupKey: string) => React.ReactNode;
  groupSummary?: (groupKey: string, groupRows: Row[]) => Row | null;
}

const GroupableTable = <Row extends Object, RenderProps extends Object>({
  columnSettings,
  renderProps,
  rows,
  initialSortState,
  fluid,
  useColumnWidthAsFlexAmount,
  onRowClick,
  onSortStateChange,
  rowClassName,
  className,
  currentPage = 0,
  pageSize: _pageSize = 0,
  totalPageCount = 0,
  onGoToPage,
  showCustomTableHead,
  groupBy,
  groupHeader,
  groupSummary,
}: Props<Row, RenderProps>) => {
  const [sortState, setSortState] = useState<SortState>(
    initialSortState ?? { ascending: true, sortedColumnIndex: 0 }
  );
  const onSortStateChangeRef = useRef(onSortStateChange);
  onSortStateChangeRef.current = onSortStateChange;

  const handleToggleSortedColumn = useCallback((columnIndex: number) => {
    setSortState((s) => {
      let newState = { ...s, sortedColumnIndex: columnIndex };
      if (columnIndex === s.sortedColumnIndex) {
        newState = { ...s, ascending: !s.ascending };
      }
      onSortStateChangeRef.current?.(newState);
      return newState;
    });
  }, []);

  const sortedRows = useMemo(() => {
    const sortFunction =
      columnSettings[sortState.sortedColumnIndex]?.sortFunction;
    const ascending = sortState.ascending ? 1 : -1;
    if (sortFunction) {
      return [...rows].sort((a, b) => sortFunction(a, b) * ascending);
    }
    return rows;
  }, [columnSettings, rows, sortState.ascending, sortState.sortedColumnIndex]);

  // Group rows if a groupBy prop is provided
  const groupedRows = useMemo(() => {
    if (!groupBy) return null;
    type RowWithIndex = { row: Row; index: number };
    return sortedRows.reduce<Record<string, RowWithIndex[]>>(
      (groups, row, idx) => {
        const key = groupBy(row);
        const prev = groups[key] || [];
        return {
          ...groups,
          [key]: [...prev, { row, index: idx }],
        };
      },
      {} as Record<string, RowWithIndex[]>
    );
  }, [groupBy, sortedRows]);
  // Map each row's original sortedRows index to its cumulative display position
  const displayIndexMap = useMemo<Record<number, number>>(() => {
    if (!groupBy || !groupedRows) return {};
    const map: Record<number, number> = {};
    let pos = 0;
    // Iterate groups to assign cumulative positions
    Object.values(groupedRows).forEach((items) => {
      items.forEach(({ index }) => {
        map[index] = pos;
        pos += 1;
      });
    });
    return map;
  }, [groupedRows, groupBy]);

  const maxPages = 10;
  const startPage = Math.max(
    Math.min(currentPage - 4, totalPageCount - maxPages),
    0
  );
  const endPage = Math.min(startPage + maxPages, totalPageCount ?? 0);

  const getPageButtons = useCallback(() => {
    return Array.from({ length: endPage - startPage }, (_, i) => {
      const page = startPage + i;
      return (
        <PageButton
          key={page}
          onClick={() => onGoToPage?.(page)}
          selected={currentPage === page}
        >
          {page + 1}
        </PageButton>
      );
    });
  }, [currentPage, endPage, onGoToPage, startPage]);

  const firstPageVisible = startPage === 0;
  const lastPageVisible = endPage === (totalPageCount ?? 0) - 1;

  return (
    <TableWrapper
      className={className}
      columnSettings={columnSettings}
      columnWidthAsFlexAmount={useColumnWidthAsFlexAmount}
    >
      <TableHead
        columnSettings={columnSettings}
        fluid={fluid}
        rows={sortedRows}
        sortAscending={sortState.ascending}
        sortedColumnIndex={sortState.sortedColumnIndex}
        toggleSort={handleToggleSortedColumn}
        showCustomTableHead={showCustomTableHead}
      />
      {groupBy && groupedRows
        ? Object.entries(groupedRows).map(([groupKey, groupItems]) => (
            <div key={groupKey}>
              {groupHeader ? (
                groupHeader(groupKey)
              ) : (
                <div className="table-group-header">{groupKey}</div>
              )}
              {groupItems.map(({ row, index }) => (
                <TableRow<Row, RenderProps>
                  key={rowClassName ? rowClassName(row) : String(index)}
                  className={rowClassName?.(row)}
                  columnSettings={columnSettings}
                  onClick={onRowClick}
                  renderProps={renderProps}
                  row={row}
                  // Row index within current page only (resets per page)
                  rowIndex={displayIndexMap[index]}
                />
              ))}
              {groupSummary && (() => {
                const summaryRow = groupSummary(groupKey, groupItems.map(({ row }) => row));
                return summaryRow ? (
                  <TableRow<Row, RenderProps>
                    key={`${groupKey}-summary`}
                    className="table-group-summary-row"
                    columnSettings={columnSettings}
                    onClick={undefined}
                    renderProps={renderProps}
                    row={summaryRow}
                    rowIndex={-1}
                  />
                ) : null;
              })()}
            </div>
          ))
        : sortedRows.map((row, i) => (
            <TableRow<Row, RenderProps>
              key={rowClassName ? rowClassName(row) : i}
              className={rowClassName?.(row)}
              columnSettings={columnSettings}
              onClick={onRowClick}
              renderProps={renderProps}
              row={row}
              // Row index within current page only (resets per page)
              rowIndex={i}
            />
          ))}
      {currentPage !== undefined && onGoToPage !== undefined && (
        <Pagination>
          {firstPageVisible ? (
            <PaginationButton
              disabled={currentPage === 0}
              onClick={() => onGoToPage!(Math.max(currentPage - 1, 0))}
            >
              {'<'}
            </PaginationButton>
          ) : (
            <PaginationButton
              disabled={currentPage === 0}
              onClick={() => onGoToPage!(0)}
            >
              {'<<'}
            </PaginationButton>
          )}
          {getPageButtons()}
          {lastPageVisible ? (
            <PaginationButton
              disabled={currentPage === (totalPageCount ?? 0) - 1}
              onClick={() =>
                onGoToPage!(Math.min(currentPage + 1, totalPageCount ?? 1))
              }
            >
              {'>'}
            </PaginationButton>
          ) : (
            <PaginationButton
              disabled={currentPage === (totalPageCount ?? 0) - 1}
              onClick={() => onGoToPage!((totalPageCount ?? 1) - 1)}
            >
              {'>>'}
            </PaginationButton>
          )}
        </Pagination>
      )}
    </TableWrapper>
  );
};

export default GroupableTable;
